import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { Scrollbars, Layout } from '@jjj/components';
import { withRouter } from 'src/containers/withRouter/withRouter';
import getOptions, {
  getSettingsRoutes,
  isMenuItemHidden,
  getSettingsMenuOptions,
  getMultiSegmentKeys,
  resolveSidebarRouteState,
} from './options';
import { getMenuItem, getSettingsMenuItems } from './mainMenu/menuItemHelpers';
import { menuIcons, settingsIcons, AngleDoubleLeftIcon, AngleDoubleRightIcon } from '../../assets/icons/sidebar';
import SidebarWrapper from './sidebar.style';
import MainMenu from './mainMenu';
import appActions from '../../redux/app/actions';
import { translationsRootKey } from '../../settings';
import { t } from '../../i18n';
import GlobalSearch from '../globalSearch/globalSearch';

const { Sider } = Layout;

const TOPBAR_HEIGHT = 40;
// Height of collapse button area (40px height + 16px margin)
const COLLAPSE_HEIGHT = 56;

const Sidebar = ({
  auth,
  app,
  height,
  startAppSettings,
  metabaseSettings,
  permissions,
  changeOpenKeys,
  changeCurrent,
  toggleCollapsed,
  setSettingsSidebarVisible,
  location,
}) => {
  // Sync sidebar state with URL changes only
  useEffect(() => {
    const settingsRoutes = getSettingsRoutes();
    const multiSegmentKeys = getMultiSegmentKeys(startAppSettings, metabaseSettings?.settings);
    const { firstSegment, matchedKey, currentPath, isOnSettingsRoute } = resolveSidebarRouteState(
      location.pathname,
      settingsRoutes,
      multiSegmentKeys,
    );

    // Sync active menu item with current URL
    if (app.current[0] !== currentPath) {
      changeCurrent([currentPath]);
    }

    // Auto-expand parent menu for multi-segment routes
    if (matchedKey && !app.openKeys.includes(firstSegment)) {
      changeOpenKeys([...app.openKeys, firstSegment]);
    }

    // Sync sidebar visibility with route type
    setSettingsSidebarVisible(isOnSettingsRoute);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleClick = e => {
    if (e.key === 'settings') {
      setSettingsSidebarVisible(true);

      return;
    }

    if (e.key === 'back-to-app') {
      setSettingsSidebarVisible(false);

      return;
    }

    if (e.key) {
      changeCurrent([e.key]);
    }
  };

  const onOpenChange = newOpenKeys => {
    const { collapsed } = app;

    if (newOpenKeys.length && collapsed) {
      toggleCollapsed();
    }

    if (!collapsed) {
      changeOpenKeys(newOpenKeys);
    }
  };

  const handleMenuItemClick = () => {
    const { view, collapsed } = app;

    if (!collapsed && (view === 'MobileView' || view === 'TabView')) {
      toggleCollapsed();
    }
  };

  const { collapsed } = app;
  const { settingsSidebarVisible } = app;
  const options = getOptions(startAppSettings, metabaseSettings?.settings, auth.role);

  // Generate menu items
  const handlers = { onMenuItemClick: handleMenuItemClick, auth, permissions };
  const mainMenuItems = options
    .filter(item => !isMenuItemHidden(item, auth.role, permissions))
    .map(option => getMenuItem(option, handlers, menuIcons));
  const settingsMenuItems = getSettingsMenuItems(
    getSettingsMenuOptions(),
    auth,
    settingsIcons,
    handleMenuItemClick,
    permissions,
  );
  const menuItems = settingsSidebarVisible ? settingsMenuItems : mainMenuItems;

  return (
    <SidebarWrapper>
      <Sider trigger={null} collapsible collapsed={collapsed} width={240} className="isomorphicSidebar">
        <Scrollbars autoHide style={{ height: height - TOPBAR_HEIGHT - COLLAPSE_HEIGHT }}>
          <GlobalSearch collapsed={collapsed} />
          <MainMenu
            items={menuItems}
            collapsed={collapsed}
            openKeys={settingsSidebarVisible ? [] : app.openKeys}
            current={app.current}
            onOpenChange={onOpenChange}
            onClick={handleClick}
          />
        </Scrollbars>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          className={cx('collapse-container', { menuCollapsed: collapsed, menuOpen: !collapsed })}
          onClick={event => {
            event.stopPropagation();
            toggleCollapsed();
          }}
        >
          <div className={cx('collapse-sidebar', { menuCollapsed: collapsed, menuOpen: !collapsed })}>
            {collapsed ? <AngleDoubleRightIcon /> : <AngleDoubleLeftIcon />}
            <span>{collapsed ? '' : t(`${translationsRootKey}.sidebar.collapse_sidebar_label`)}</span>
          </div>
        </div>
      </Sider>
    </SidebarWrapper>
  );
};

export default connect(
  state => ({
    auth: state.Auth,
    app: state.App,
    height: state.App.height,
    startAppSettings: state.StartAppSettings,
    metabaseSettings: state.MetabaseSettings,
    permissions: state.Permissions.data,
  }),
  dispatch => ({
    changeOpenKeys: data => dispatch(appActions.changeOpenKeys(data)),
    changeCurrent: data => dispatch(appActions.changeCurrent(data)),
    toggleCollapsed: () => dispatch(appActions.toggleCollapsed()),
    setSettingsSidebarVisible: visible => dispatch(appActions.setSettingsSidebarVisible(visible)),
  }),
)(withRouter(Sidebar));
